/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { ImmagineMetadataDto } from '../schemas/immagineMetadataDto.ts';
import type { PostImmaginiBody } from '../schemas/postImmaginiBody.ts';
import type { PostImmaginiParams } from '../schemas/postImmaginiParams.ts';
import type { PutImmaginiIdBody } from '../schemas/putImmaginiIdBody.ts';
import type { PutImmaginiIdParams } from '../schemas/putImmaginiIdParams.ts';
import type { WsProblema } from '../schemas/wsProblema.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce i metadati dell'immagine.
 * @summary Dettaglio immagine
 */
export const getImmaginiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ImmagineMetadataDto>(
    {
      url: `/framework-cfg-procedimenti/immagini/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetImmaginiIdQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/immagini/${id}`] as const;

export const getGetImmaginiIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getImmaginiId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getImmaginiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getImmaginiId>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetImmaginiIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getImmaginiId>>> = ({
    signal,
  }) => getImmaginiId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetImmaginiIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getImmaginiId>>
>;
export type GetImmaginiIdQueryError = ErrorType<WsProblema>;

/**
 * @summary Dettaglio immagine
 */
export const useGetImmaginiId = <
  TData = Awaited<ReturnType<typeof getImmaginiId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getImmaginiId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetImmaginiIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna il file dell'immagine e, opzionalmente, i metadati.
 * @summary Aggiorna immagine
 */
export const putImmaginiId = (
  id: string,
  putImmaginiIdBody: PutImmaginiIdBody,
  params?: PutImmaginiIdParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  if (putImmaginiIdBody.file !== undefined) {
    formData.append('file', putImmaginiIdBody.file);
  }

  return apiClientRequest<ImmagineMetadataDto>(
    {
      url: `/framework-cfg-procedimenti/immagini/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
    },
    options,
  );
};

export const getPutImmaginiIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putImmaginiId>>,
    TError,
    { id: string; data: PutImmaginiIdBody; params?: PutImmaginiIdParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putImmaginiId>>,
  TError,
  { id: string; data: PutImmaginiIdBody; params?: PutImmaginiIdParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putImmaginiId>>,
    { id: string; data: PutImmaginiIdBody; params?: PutImmaginiIdParams }
  > = props => {
    const { id, data, params } = props ?? {};

    return putImmaginiId(id, data, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutImmaginiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putImmaginiId>>
>;
export type PutImmaginiIdMutationBody = PutImmaginiIdBody;
export type PutImmaginiIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Aggiorna immagine
 */
export const usePutImmaginiId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putImmaginiId>>,
    TError,
    { id: string; data: PutImmaginiIdBody; params?: PutImmaginiIdParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutImmaginiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancella immagine (record + file).
 * @summary Elimina immagine
 */
export const deleteImmaginiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-procedimenti/immagini/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteImmaginiIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteImmaginiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteImmaginiId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteImmaginiId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteImmaginiId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteImmaginiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteImmaginiId>>
>;

export type DeleteImmaginiIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina immagine
 */
export const useDeleteImmaginiId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteImmaginiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteImmaginiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Carica una nuova immagine e crea il relativo record di metadati.
 * @summary Carica immagine
 */
export const postImmagini = (
  postImmaginiBody: PostImmaginiBody,
  params: PostImmaginiParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append('file', postImmaginiBody.file);

  return apiClientRequest<ImmagineMetadataDto>(
    {
      url: `/framework-cfg-procedimenti/immagini`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
    },
    options,
  );
};

export const getPostImmaginiMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postImmagini>>,
    TError,
    { data: PostImmaginiBody; params: PostImmaginiParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postImmagini>>,
  TError,
  { data: PostImmaginiBody; params: PostImmaginiParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postImmagini>>,
    { data: PostImmaginiBody; params: PostImmaginiParams }
  > = props => {
    const { data, params } = props ?? {};

    return postImmagini(data, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostImmaginiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postImmagini>>
>;
export type PostImmaginiMutationBody = PostImmaginiBody;
export type PostImmaginiMutationError = ErrorType<WsProblema>;

/**
 * @summary Carica immagine
 */
export const usePostImmagini = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postImmagini>>,
    TError,
    { data: PostImmaginiBody; params: PostImmaginiParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostImmaginiMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Scarica il file binario dell'immagine.
 * @summary Scarica file immagine
 */
export const getImmaginiIdFile = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-procedimenti/immagini/${id}/file`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetImmaginiIdFileQueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/immagini/${id}/file`] as const;

export const getGetImmaginiIdFileQueryOptions = <
  TData = Awaited<ReturnType<typeof getImmaginiIdFile>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getImmaginiIdFile>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getImmaginiIdFile>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetImmaginiIdFileQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getImmaginiIdFile>>
  > = ({ signal }) => getImmaginiIdFile(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetImmaginiIdFileQueryResult = NonNullable<
  Awaited<ReturnType<typeof getImmaginiIdFile>>
>;
export type GetImmaginiIdFileQueryError = ErrorType<WsProblema>;

/**
 * @summary Scarica file immagine
 */
export const useGetImmaginiIdFile = <
  TData = Awaited<ReturnType<typeof getImmaginiIdFile>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getImmaginiIdFile>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetImmaginiIdFileQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce il file immagine codificato in BASE64.
 * @summary Immagine in base64
 */
export const getImmaginiIdBase64 = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-procedimenti/immagini/${id}/base64`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetImmaginiIdBase64QueryKey = (id: string) =>
  [`/framework-cfg-procedimenti/immagini/${id}/base64`] as const;

export const getGetImmaginiIdBase64QueryOptions = <
  TData = Awaited<ReturnType<typeof getImmaginiIdBase64>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getImmaginiIdBase64>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getImmaginiIdBase64>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetImmaginiIdBase64QueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getImmaginiIdBase64>>
  > = ({ signal }) => getImmaginiIdBase64(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetImmaginiIdBase64QueryResult = NonNullable<
  Awaited<ReturnType<typeof getImmaginiIdBase64>>
>;
export type GetImmaginiIdBase64QueryError = ErrorType<WsProblema>;

/**
 * @summary Immagine in base64
 */
export const useGetImmaginiIdBase64 = <
  TData = Awaited<ReturnType<typeof getImmaginiIdBase64>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getImmaginiIdBase64>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetImmaginiIdBase64QueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
